import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconHistory, IconDownload, IconLoader2, IconCalendar,
} from "@tabler/icons-react";
import { api, formatApiError, API } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const MODULES = [
  { id: "circulo", label_es: "Círculo", label_en: "Círculo" },
  { id: "aula", label_es: "Aula", label_en: "Aula" },
  { id: "sintonia", label_es: "Sintonía", label_en: "Sintonía" },
  { id: "brujula", label_es: "Brújula", label_en: "Brújula" },
  { id: "sello", label_es: "Sello", label_en: "Sello" },
];

const PRESETS = [
  { id: "week", label_es: "Última semana", label_en: "Last week" },
  { id: "month", label_es: "Último mes", label_en: "Last month" },
  { id: "3months", label_es: "Últimos 3 meses", label_en: "Last 3 months" },
  { id: "6months", label_es: "Últimos 6 meses", label_en: "Last 6 months" },
  { id: "year", label_es: "Último año", label_en: "Last year" },
  { id: "all", label_es: "Todo el historial", label_en: "All history" },
];

export default function Historial() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [module, setModule] = useState("circulo");
  const [preset, setPreset] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildQs = useCallback(() => {
    const params = new URLSearchParams();
    if (customFrom || customTo) {
      if (customFrom) params.set("date_from", `${customFrom}T00:00:00+00:00`);
      if (customTo) params.set("date_to", `${customTo}T23:59:59+00:00`);
    } else if (preset) {
      params.set("preset", preset);
    }
    params.set("limit", "500");
    return params.toString();
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/history/${module}?${buildQs()}`);
      setRows(data.rows || []);
      setCount(data.count || 0);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [module, buildQs]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const url = `${API}/admin/history/${module}/csv?${buildQs()}`;
    // Cookies auth (withCredentials) — we can use fetch + blob to keep cookies
    fetch(url, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `historial_${module}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((e) => setError(e.message));
  };

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="historial-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Historial · Registros por módulo" : "History · Records per module"}</span>
      </div>

      <div className="wrap-app max-w-[1160px] py-10">
        {/* Filters */}
        <div className="card">
          <p className="lbl m-0 mb-2 flex items-center gap-2"><IconHistory size={12} /> {lang === "es" ? "Módulo" : "Module"}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {MODULES.map((m) => (
              <button
                key={m.id}
                onClick={() => setModule(m.id)}
                className={`px-3 py-1.5 text-[12.5px] rounded-lg border ${module === m.id ? "border-teal bg-teal-soft text-teal-deep" : "border-line bg-white text-muted"}`}
                data-testid={`hist-mod-${m.id}`}
              >
                {lang === "es" ? m.label_es : m.label_en}
              </button>
            ))}
          </div>

          <p className="lbl m-0 mb-2 flex items-center gap-2"><IconCalendar size={12} /> {lang === "es" ? "Rango de fechas" : "Date range"}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => { setPreset(p.id); setCustomFrom(""); setCustomTo(""); }}
                className={`px-3 py-1.5 text-[12px] rounded-lg border ${preset === p.id && !customFrom && !customTo ? "border-teal bg-teal-soft text-teal-deep" : "border-line bg-white text-muted"}`}
                data-testid={`hist-preset-${p.id}`}
              >
                {lang === "es" ? p.label_es : p.label_en}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 max-w-[420px]">
            <div className="field m-0">
              <label className="text-tert text-[11px]">{lang === "es" ? "Desde (custom)" : "From (custom)"}</label>
              <input type="date" className="input" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="hist-from" />
            </div>
            <div className="field m-0">
              <label className="text-tert text-[11px]">{lang === "es" ? "Hasta (custom)" : "To (custom)"}</label>
              <input type="date" className="input" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="hist-to" />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <p className="text-tert text-[13px]">
              {loading ? (lang === "es" ? "Cargando..." : "Loading...") : `${count} ${lang === "es" ? "registros" : "records"}`}
            </p>
            <button
              onClick={downloadCsv}
              disabled={loading || count === 0}
              className="btn text-[12.5px]"
              data-testid="hist-download-csv"
            >
              <IconDownload size={12} /> {lang === "es" ? "Descargar CSV UTF-8" : "Download CSV UTF-8"}
            </button>
          </div>
        </div>

        {error && <p className="text-errtext text-[13px] mt-4">{error}</p>}

        {/* Table */}
        <div className="mt-6 card p-0 overflow-hidden">
          {loading ? (
            <div className="p-6"><IconLoader2 className="animate-spin" /></div>
          ) : rows.length === 0 ? (
            <p className="p-6 text-tert text-[13px]" data-testid="hist-empty">
              {lang === "es" ? "Sin registros en el rango seleccionado." : "No records in the selected range."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="hist-table">
                <thead>
                  <tr className="text-left border-b border-line bg-teal-soft/40">
                    <th className="p-3">{lang === "es" ? "Fecha" : "Date"}</th>
                    <th className="p-3">{lang === "es" ? "Tipo" : "Type"}</th>
                    <th className="p-3">{lang === "es" ? "Autor" : "Author"}</th>
                    <th className="p-3">{lang === "es" ? "Sujeto" : "Subject"}</th>
                    <th className="p-3">{lang === "es" ? "Etiquetas" : "Tags"}</th>
                    <th className="p-3">{lang === "es" ? "Contenido" : "Content"}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-line hover:bg-teal-soft/20" data-testid={`hist-row-${i}`}>
                      <td className="p-3 font-mono text-[11.5px] whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString(lang === "es" ? "es-HN" : "en-US", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="p-3">
                        <span className="tag">{r.type}</span>
                      </td>
                      <td className="p-3">
                        <div>{r.author}</div>
                        <div className="text-tert text-[10.5px]">{r.author_role}</div>
                      </td>
                      <td className="p-3 font-medium">{r.subject}</td>
                      <td className="p-3 text-tert font-mono text-[11px]">{r.tags}</td>
                      <td className="p-3 text-muted max-w-[420px] leading-relaxed">{r.content}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
